import type { ApplicationOptions, Container, Texture } from 'pixi.js';
import { Application } from 'pixi.js';
import { Signal } from 'typed-signals';
import type { DeviceSafeArea } from './platform/Platform';
import { setSafeAreaInset } from '../safearea/SafeAreaProvider';

export const safeAreaDimensions = {
  width: 780,
  height: 1280,
  minHorizontalWidth: 1700,
};

export function isHorizontal(width: number): boolean {
  return width >= safeAreaDimensions.minHorizontalWidth;
}

export class RendererPlugin {
  public readonly name = 'Renderer';

  #view: HTMLElement;

  #quality: Quality = 'high';

  private fpsWarning = 0;

  private readonly updateBinding = this.update.bind(this);

  private readonly warnFPS = 30;

  private readonly warnFPSDelaySeconds = 5;

  public safeArea: DeviceSafeArea | null = null;

  public readonly appSize = { width: safeAreaDimensions.width, height: safeAreaDimensions.height, scale: 1 };

  public pixi: Application;

  public onResize = new Signal<(w: number, h: number) => void>();

  constructor(private readonly options?: RendererSettings) {
    this.pixi = new Application();

    this.#view = this.createView();
  }

  public async init(): Promise<void> {
    await this.pixi.init({
      resolution: 1,
      backgroundAlpha: 0,
      antialias: true,
      hello: true,
      ...this.options?.pixi,
    });
    
    this.applySafeArea();

    this.#view.appendChild(this.pixi.canvas);

    this.subscribeResize();
    this.resize();

    this.pixi.ticker.maxFPS = 60;
    this.pixi.ticker.start();

    this.trackFPS();
  }

  public setView(view: HTMLElement | undefined): void {
    if (!view) {
      return;
    }

    document.body.removeChild(this.#view);

    this.#view = view;
    this.#view.appendChild(this.pixi.view);

    this.resize();
  }

  protected trackFPS(): void {
    this.pixi.ticker.add(this.updateBinding, this);
  }

  protected stopTrackFPS(): void {
    this.fpsWarning = 0;
    this.pixi.ticker.remove(this.updateBinding);
  }

  protected update(): void {
    const warnFPSDelay = 60 * this.warnFPSDelaySeconds;

    if (this.pixi.ticker.FPS < this.warnFPS) {
      this.fpsWarning += 1;

      if (this.fpsWarning >= warnFPSDelay) {
        this.stopTrackFPS();

        this.quality = 'low';
      }
    }
  }

  protected subscribeResize(): void {
    window.addEventListener('resize', () => {
      this.resize();
    });
    window.addEventListener('deviceorientation', () => {
      this.resize();
    });
    try {
      window.addEventListener('orientationchange', () => {
        this.resize();
      });
    } catch (error) {
      console.error('failed to subscribe to orientationchange:', error);
    }
    window.visualViewport?.addEventListener('resize', () => {
      this.resize();
    });
  }

  protected resize(): void {
    const { width, height } = safeAreaDimensions;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const { renderer, canvas } = this.pixi;

    if (windowWidth === this.width && windowHeight === this.height) return;

    const mw = width / windowWidth;
    const mh = height / windowHeight;
    const scale = Math.max(mw, mh);

    const newWidth = Math.round(windowWidth * scale);
    const newHeight = Math.round(windowHeight * scale);

    renderer.resize(newWidth, newHeight, 1);
    canvas.style.width = `${windowWidth}px`;
    canvas.style.height = `${windowHeight}px`;
    window.scrollTo(0, 0);

    this.appSize.width = newWidth;
    this.appSize.height = newHeight;

    this.onResize.emit(newWidth, newHeight);
  }

  private createView(): HTMLElement {
    const view = document.createElement('div');

    view.id = 'renderer_initial_view';
    view.style.position = 'fixed';
    view.style.width = '100%';
    view.style.height = '100%';

    document.body.appendChild(view);

    return view;
  }

  set quality(quality: Quality) {
    this.#quality = quality;
    // plugins.log?.error('Low FPS', this.pixi.ticker.FPS);
  }

  get quality(): Quality {
    return this.#quality;
  }

  get width(): number {
    return this.appSize.width;
  }

  get height(): number {
    return this.appSize.height;
  }

  public generateTexture(container: Container): Texture {
    return this.pixi.renderer.textureGenerator.generateTexture({
      target: container,
      antialias: true,
    });
  }

  private applySafeArea(): void {
    const { safeArea } = this;
    if (!safeArea) {
      setSafeAreaInset({ top: 0, right: 0, bottom: 0, left: 0 });
      this.resize();
      return;
    }
    safeArea.init().then(() => {
      setSafeAreaInset(safeArea.getSafeAreaInset());
    }).catch((error) => {
      console.error('Error applying safe area:', error);
    }).finally(() => {
      this.resize();
    });
  }
}

export type Quality = 'low' | 'high';

type RendererSettings = {
  pixi?: Partial<ApplicationOptions>;
  minWidth?: number;
  minHeight?: number;
};

export function getResolution(): number {
  /*
    let resolution = Math.max(window.devicePixelRatio, 2);

    if (resolution % 1 !== 0) {
        resolution = 2;
    }

    return resolution;
    */
  return 1;
}
